import type {
  TGetAIContentV2RequestPayload,
  TGetAIContentV2ResponsePayload,
  TGetExperienceRequestPayload,
  TGetExperienceResponsePayload,
  TGetStepRequestPayload,
  TGetStepResponsePayload,
  TMessageInfo,
  TStepInfo,
  TWidget,
} from '@cascade/api-client/ge-v2';
import {
  areArraysEqual,
  areRecordsEqual,
  isValidDecimal,
} from '@cascade/core/utils';
import type { TNumberRange } from '@cascade/core/utils';
import type { TTagGroup } from '#providers/projects/index.ts';
import { createQueryKey } from '#providers/query/index.ts';
import type { TQueryKeyPrefix } from '#providers/query/index.ts';
import type {
  TAnswer,
  TAnswers,
  TConditionalWidget,
  TOrdered,
  TWidgetsTransform,
} from './Experience.types.ts';

export const experiencesQueryPrefix = [
  'geV2',
  'getExperienceV2',
] as const satisfies TQueryKeyPrefix<'geV2'>;

export const getExperienceQuery = (projectId: string, experienceId: string) => {
  const experienceRequest: TGetExperienceRequestPayload = {
    project_id: projectId,
    experience_id: experienceId,
    include_steps: true,
    include_conversation: true,
  };

  const experienceKey = createQueryKey(
    'geV2',
    'getExperienceV2',
    experienceRequest,
  );

  return { experienceRequest, experienceKey };
};

export const getMessageKey = ({
  message_id: messageId,
  sender,
}: TMessageInfo): string => `${messageId}/${sender ?? ''}`;

export const appendMessages = (
  experience: TGetExperienceResponsePayload,
  messages: TMessageInfo[],
): TGetExperienceResponsePayload => {
  const heldMessages = experience.messages ?? [];
  const heldMessageKeys = heldMessages.map(getMessageKey);
  const heldKeys = new Set(heldMessageKeys);

  const isNewMessage = (message: TMessageInfo): boolean => {
    const messageKey = getMessageKey(message);

    return !message.message_id || !heldKeys.has(messageKey);
  };

  const newMessages = messages.filter(isNewMessage);

  return { ...experience, messages: [...heldMessages, ...newMessages] };
};

export const getStepQueries = (
  projectId: string,
  experienceId: string,
  stepId: string,
  tagValues: TTagGroup,
  isMobile?: boolean,
) => {
  const stepRequest: TGetStepRequestPayload = {
    project_id: projectId,
    experience_id: experienceId,
    step_id: stepId,
    include_sections: true,
    include_suggested_questions: true,
    is_mobile: isMobile,
  };

  const contentRequest: TGetAIContentV2RequestPayload = {
    project_id: projectId,
    experience_id: experienceId,
    step_id: stepId,
    is_mobile: isMobile,
  };

  const stepKey = [
    ...createQueryKey('geV2', 'getStepV2', stepRequest),
    tagValues,
  ];

  const contentKey = createQueryKey('geV2', 'getAiContentV2', contentRequest);

  return { stepRequest, stepKey, contentRequest, contentKey };
};

export const getSelectedOptionIds = (answer?: TAnswer): string[] => {
  if (answer === undefined) {
    return [];
  }

  return Array.isArray(answer) ? answer : [answer];
};

export const getInputValue = (answer?: TAnswer): string =>
  typeof answer === 'string' ? answer : '';

export const getInputRange = ({
  min_value: min,
  max_value: max,
}: TWidget): TNumberRange => ({
  min: min ?? undefined,
  max: max ?? undefined,
});

export const isValidInputValue = (widget: TWidget, value: string): boolean => {
  if (widget.input_type !== 'number' || value === '') {
    return true;
  }

  const range = getInputRange(widget);

  return isValidDecimal(value, range);
};

export const isWidgetVisible = (
  answers: TAnswers,
  { display_condition: condition }: TConditionalWidget,
): boolean => {
  if (!condition?.widget_id || condition.selected_option_ids.length === 0) {
    return true;
  }

  const answer = answers[condition.widget_id];
  const selectedOptionIds = getSelectedOptionIds(answer);

  return selectedOptionIds.some((optionId) =>
    condition.selected_option_ids.includes(optionId),
  );
};

const isAnswered = (answer?: TAnswer): boolean =>
  answer !== undefined && answer.length > 0;

const getEmptyAnswer = ({
  widget_type: widgetType,
}: TConditionalWidget): TAnswer => (widgetType === 'select' ? [] : '');

export const clearHiddenAnswers = (
  answers: TAnswers,
  widgets: TConditionalWidget[],
): TAnswers => {
  const hidden = widgets.find(
    (widget) =>
      isAnswered(answers[widget.id]) && !isWidgetVisible(answers, widget),
  );

  if (!hidden) {
    return answers;
  }

  const emptyAnswer = getEmptyAnswer(hidden);

  return clearHiddenAnswers({ ...answers, [hidden.id]: emptyAnswer }, widgets);
};

const isSameAnswer = (left?: TAnswer, right?: TAnswer): boolean => {
  if (Array.isArray(left) && Array.isArray(right)) {
    return areArraysEqual(left, right);
  }

  return left === right;
};

export const areAnswersEqual = (left: TAnswers, right: TAnswers): boolean =>
  areRecordsEqual(left, right, isSameAnswer);

export const isWidgetPending = (widget: TWidget): boolean => {
  if (widget.widget_type === 'select') {
    return widget.ai_recommendations === null;
  }

  return (
    widget.widget_type === 'text' &&
    widget.content_type === 'aiGenerated' &&
    widget.content === null
  );
};

const applyWidgetContent = (
  widget: TWidget,
  {
    ai_generated_content: texts,
    ai_recommendations: recommendations,
  }: TGetAIContentV2ResponsePayload,
): TWidget => {
  if (!isWidgetPending(widget)) {
    return widget;
  }

  if (widget.widget_type === 'text') {
    return { ...widget, content: texts[widget.id] ?? '' };
  }

  const recommended = recommendations[widget.id] ?? [];
  const options = widget.options?.map((option) => ({
    ...option,
    ai_recommended: recommended.includes(option.id),
  }));

  return { ...widget, ai_recommendations: recommended, options };
};

const mapSectionWidgets = (
  step: TGetStepResponsePayload,
  transform: TWidgetsTransform,
): TGetStepResponsePayload => {
  const sections = step.sections?.map((section) => ({
    ...section,
    widgets: transform(section.widgets),
  }));

  return { ...step, sections };
};

export const applyGeneratedContent = (
  step: TGetStepResponsePayload,
  content: TGetAIContentV2ResponsePayload,
): TGetStepResponsePayload =>
  mapSectionWidgets(step, (widgets) =>
    widgets.map((widget) => applyWidgetContent(widget, content)),
  );

export const getHiddenWidgetIds = (
  step: TGetStepResponsePayload,
  answers: TAnswers,
): string[] =>
  getStepWidgets(step)
    .filter((widget) => !isWidgetVisible(answers, widget))
    .map((widget) => widget.id);

export const removeHiddenWidgets = (
  step: TGetStepResponsePayload,
  hiddenWidgetIds: string[],
): TGetStepResponsePayload =>
  mapSectionWidgets(step, (widgets) =>
    widgets.filter((widget) => !hiddenWidgetIds.includes(widget.id)),
  );

const compareByOrder = (left: TOrdered, right: TOrdered): number =>
  left.order - right.order;

export const sortStep = (
  step: TGetStepResponsePayload,
): TGetStepResponsePayload => {
  const stepWithSortedWidgets = mapSectionWidgets(step, (widgets) =>
    widgets.toSorted(compareByOrder),
  );

  const sections = stepWithSortedWidgets.sections?.toSorted(compareByOrder);

  return { ...stepWithSortedWidgets, sections };
};

export const getStepWidgets = (step: TGetStepResponsePayload): TWidget[] =>
  (step.sections ?? []).flatMap((section) => section.widgets);

export const hasPendingWidgets = (step: TGetStepResponsePayload): boolean =>
  getStepWidgets(step).some(isWidgetPending);

export const getSeededAnswers = (widgets: TWidget[]): TAnswers => {
  const seeded: TAnswers = {};

  for (const widget of widgets) {
    if (widget.widget_type === 'input' && widget.input_value) {
      seeded[widget.id] = widget.input_value;
    }

    if (widget.widget_type === 'select') {
      const selected = (widget.options ?? [])
        .filter((option) => option.selected)
        .map((option) => option.id);

      if (selected.length > 0) {
        seeded[widget.id] = selected;
      }
    }
  }

  return seeded;
};

export const getMainSteps = (steps: TStepInfo[] = []): TStepInfo[] => {
  const orderedSteps = steps
    .filter((step) => step.order > 0)
    .sort(compareByOrder);

  if (orderedSteps.length > 0) {
    return orderedSteps;
  }

  return steps.slice(0, 1);
};

export const isMainStepId = (
  mainSteps: TStepInfo[],
  stepId?: string,
): boolean => mainSteps.some((step) => step.id === stepId);

export const getCurrentStepId = (
  visitedStepIds: string[],
  mainSteps: TStepInfo[],
): string | undefined => visitedStepIds.at(-1) ?? mainSteps.at(0)?.id;

export const getPreviousStepId = (
  visitedStepIds: string[],
  mainSteps: TStepInfo[],
  currentStepId?: string,
): string | undefined => {
  const mainIndex = mainSteps.findIndex((step) => step.id === currentStepId);

  if (mainIndex === -1) {
    return visitedStepIds.findLast((stepId) => isMainStepId(mainSteps, stepId));
  }

  return mainSteps[mainIndex - 1]?.id;
};

export const getNextStepId = (
  mainSteps: TStepInfo[],
  currentStepId?: string,
): string | undefined => {
  const mainIndex = mainSteps.findIndex((step) => step.id === currentStepId);

  if (mainIndex === -1) {
    return undefined;
  }

  return mainSteps[mainIndex + 1]?.id;
};

export const getCompletedStepIds = (
  visitedStepIds: string[],
  currentStepId?: string,
): string[] => {
  const leftStepIds = visitedStepIds.filter(
    (stepId) => stepId !== currentStepId,
  );

  return [...new Set(leftStepIds)];
};
